/**
 * useSystemDateRollover
 *
 * Subscribes to SSE events for system date rollover lifecycle:
 *
 *   system_date_rollover_warning — Shows a non-dismissable full-screen warning
 *                                  modal (~5 minutes before midnight).
 *   system_date_force_logout     — Triggers automatic logout at midnight after
 *                                  the date has been advanced.
 *
 * Only mounted by SystemDateRolloverGuard in App.tsx when user is authenticated,
 * so clientId is always a non-empty string when the hook initialises.
 */

import { useState, useCallback, useRef } from 'react';
import { useAuth } from '../components/auth/AuthContext';
import { useEventStreamSimple } from './useEventStreamSimple';

interface UseSystemDateRolloverResult {
  showRolloverWarning: boolean;
  forcingLogout: boolean;
}

export function useSystemDateRollover(clientId: string): UseSystemDateRolloverResult {
  const { logout } = useAuth();
  const [showRolloverWarning, setShowRolloverWarning] = useState(false);
  const [forcingLogout, setForcingLogout] = useState(false);

  // Record when this session started so replayed historical events are ignored.
  // The SSE stream may replay recent events on reconnect; a force_logout event
  // from a previous rollover must not kick the user out of their new session.
  const sessionStartRef = useRef(Date.now());

  const handleEvent = useCallback((eventData: any) => {
    if (eventData.type === 'system_date_rollover_warning') {
      // Ignore if the event was emitted before this session started (stale replay)
      const eventTime = eventData.timestamp ? new Date(eventData.timestamp).getTime() : Infinity;
      if (eventTime < sessionStartRef.current) return;
      setShowRolloverWarning(true);
    } else if (eventData.type === 'system_date_force_logout') {
      // Ignore if the event was emitted before this session started
      const eventTime = eventData.timestamp ? new Date(eventData.timestamp).getTime() : Infinity;
      if (eventTime < sessionStartRef.current) return;

      setShowRolloverWarning(false);
      setForcingLogout(true);
      // Brief delay so user sees "Cerrando sesión..." before Firebase signOut triggers
      setTimeout(() => {
        logout();
      }, 1500);
    }
  }, [logout]);

  useEventStreamSimple({
    clientId,
    eventTypes: ['system_date_rollover_warning', 'system_date_force_logout'],
    onEvent: handleEvent,
  });

  return { showRolloverWarning, forcingLogout };
}
